import type { PopDisplays_JobStatus } from 'c-sdk';

export const paginationTag = 'task-queue-list-search';

export const sourceTypeFilterKey = 'filter.statusable_type';
export const sourceIdFilterKey = 'filter.statusable_id';

export type JobState = 'queued' | 'running' | 'succeeded' | 'failed' | 'stuck';

/**
 * A job that is still open long after anything of its kind has ever taken.
 *
 * `stuck_after_seconds` comes from the API, derived from a sliding 28-day window of that task
 * family's own run times, because "too long" means nothing globally — a Talon import legitimately
 * runs for an hour and a half, a Campaign Sync for half a minute. The API falls back to a figure
 * above the slowest run ever recorded when a family has too few samples to have a baseline, so an
 * unrecognised task is never accused on the strength of a guess.
 */
export const isStuck = (entity: PopDisplays_JobStatus) => {
    if (entity.completed_at != null) return false;

    const threshold = entity.stuck_after_seconds;
    if (threshold == null) return false;

    const since = entity.started_at ?? entity.created_at;
    if (since == null) return false;

    const openFor = (Date.now() - new Date(since).getTime()) / 1000;

    return Number.isFinite(openFor) && openFor > threshold;
};

/**
 * The API maps ECS lifecycle states onto its own vocabulary before storing them
 * (CheckFargateTasks::updateEcsStatuses): PROVISIONING/ACTIVATING/PENDING -> Starting,
 * RUNNING -> Online, DEACTIVATING/STOPPING/DEPROVISIONING -> Stopping, STOPPED/DELETED -> Offline.
 * Anything it doesn't recognise is stored verbatim, so compare case insensitively and keep the raw
 * ECS names here too.
 */
const failedEcsStatuses = [
    'stopping',
    'offline',
    'stopped',
    'deprovisioning',
    'deleted',
    'deactivating',
];

/**
 * Anchored to the start of the note on purpose. `JobStatus::errored()` writes `ERROR - <detail>`
 * and `completed()` writes `Completed - <detail>`, so the prefix is the real discriminator.
 *
 * Searching anywhere in the note instead matches the tallies in a success summary — a completed
 * Talon batch reads `Completed - Batch 20260811 - ... - Failed: 0 - Orderline errors: 0 - ...`,
 * where every count is zero and "errors" is a column heading, not an outcome.
 */
const errorNotePrefix = /^\s*(error|failed|stopped)\b/i;

export const isErrorNote = (entity: PopDisplays_JobStatus) =>
    errorNotePrefix.test(entity.notes ?? '');

export const hasFailedEcsTask = (entity: PopDisplays_JobStatus) =>
    failedEcsStatuses.includes((entity.ecs_task_status ?? '').toLowerCase()) &&
    entity.completed_at == null;

/**
 * The error note is checked *before* completion, because `JobStatus::errored()` stamps
 * `completed_at` exactly as `completed()` does. Checking completion first therefore made the failed
 * state unreachable for every job that reported its failure through the model's own helper — a
 * 30-day sample held 166 such rows, carrying notes like `ERROR Trying to access array offset on
 * null` and `ERROR: skeleton_merge.py failed (exit 1)`, all rendering green under a "Completed"
 * chip.
 *
 * A job that genuinely succeeded is unaffected: `completed()` writes `Completed - <detail>`, which
 * the prefix does not match.
 *
 * `stuck` sits below `failed` deliberately. A container that stopped without completing is a
 * failure and already reports as one through `hasFailedEcsTask`; stuck is the weaker claim reserved
 * for a job that is merely still open long past any plausible run time, with nothing saying why.
 */
export const deriveJobState = (entity: PopDisplays_JobStatus): JobState => {
    if (isErrorNote(entity) || hasFailedEcsTask(entity)) return 'failed';
    if (entity.completed_at != null) return 'succeeded';
    if (isStuck(entity)) return 'stuck';
    if (entity.started_at != null) return 'running';
    return 'queued';
};

export const canStopJob = (entity: PopDisplays_JobStatus) => {
    if (entity.deleted_at != null) return false;
    const state = deriveJobState(entity);
    // A stuck job is still stoppable — it is open, and stopping it is the point of noticing it.
    return state === 'queued' || state === 'running' || state === 'stuck';
};
